import type { ApiResponse, ApiError, AuthTokens } from '@piano/shared';

// In production, use the full API URL; in dev, use the local proxy
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiClient {
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers || {}),
    };

    if (!skipAuth && this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new ApiRequestError(
        error.error?.message || 'An error occurred',
        error.error?.code || 'UNKNOWN_ERROR',
        response.status
      );
    }

    return (data as ApiResponse<T>).data;
  }

  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const apiClient = new ApiClient();

// Auth API
export const authApi = {
  register: (data: { email: string; password: string; name: string }) =>
    apiClient.post<{ user: { id: string; email: string; name: string }; tokens: AuthTokens }>(
      '/auth/register',
      data,
      { skipAuth: true }
    ),

  login: (data: { email: string; password: string }) =>
    apiClient.post<{ user: { id: string; email: string; name: string }; tokens: AuthTokens }>(
      '/auth/login',
      data,
      { skipAuth: true }
    ),

  logout: () => apiClient.post('/auth/logout'),

  refresh: () => apiClient.post<{ tokens: AuthTokens }>('/auth/refresh', {}, { skipAuth: true }),

  me: () =>
    apiClient.get<{ user: { id: string; email: string; name: string; avatarUrl?: string } }>(
      '/auth/me'
    ),
};

// Teams API
export interface TeamMember {
  userId: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  avatarUrl?: string;
}

export const teamsApi = {
  list: () =>
    apiClient.get<{ teams: Array<{ id: string; name: string; slug: string; ownerId: string }> }>(
      '/teams'
    ),

  create: (data: { name: string; slug: string }) =>
    apiClient.post<{ team: { id: string; name: string; slug: string } }>('/teams', data),

  get: (teamId: string) =>
    apiClient.get<{ team: { id: string; name: string; slug: string; ownerId: string } }>(
      `/teams/${teamId}`
    ),

  update: (teamId: string, data: { name?: string }) =>
    apiClient.patch<{ team: { id: string; name: string; slug: string } }>(`/teams/${teamId}`, data),

  getMembers: (teamId: string) =>
    apiClient.get<{ members: TeamMember[] }>(`/teams/${teamId}/members`),

  inviteMember: (teamId: string, data: { email: string; role: 'admin' | 'member' }) =>
    apiClient.post<{ members: TeamMember[] }>(`/teams/${teamId}/invite`, data),

  removeMember: (teamId: string, userId: string) =>
    apiClient.delete(`/teams/${teamId}/members/${userId}`),
};

// Projects API
export const projectsApi = {
  list: (teamId: string) =>
    apiClient.get<{
      projects: Array<{
        id: string;
        name: string;
        description?: string;
        status: string;
        keywords?: string[];
      }>;
    }>(`/projects?teamId=${teamId}`),

  create: (data: {
    teamId: string;
    name: string;
    description?: string;
    color?: string;
    keywords?: string[];
  }) => apiClient.post<{ project: { id: string; name: string } }>('/projects', data),

  get: (projectId: string) =>
    apiClient.get<{
      project: {
        id: string;
        name: string;
        description?: string;
        teamId: string;
        keywords?: string[];
      };
    }>(`/projects/${projectId}`),

  update: (
    projectId: string,
    data: { name?: string; description?: string; status?: string; keywords?: string[] }
  ) =>
    apiClient.patch<{ project: { id: string; name: string; keywords?: string[] } }>(
      `/projects/${projectId}`,
      data
    ),

  delete: (projectId: string) => apiClient.delete(`/projects/${projectId}`),

  getStats: (projectId: string) =>
    apiClient.get<{
      stats: { total: number; byStatus: Record<string, number>; avgCompletion: number };
    }>(`/projects/${projectId}/stats`),
};

// Tasks API
export const tasksApi = {
  list: (params: { projectId?: string; status?: string; priority?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });
    return apiClient.get<{ tasks: Array<unknown> }>(`/tasks?${searchParams.toString()}`);
  },

  getKanban: (projectId: string) =>
    apiClient.get<{ tasks: Record<string, Array<unknown>> }>(`/tasks/kanban/${projectId}`),

  create: (data: {
    projectId: string;
    title: string;
    description?: string;
    priority?: string;
    assigneeId?: string;
  }) => apiClient.post<{ task: unknown }>('/tasks', data),

  get: (taskId: string) => apiClient.get<{ task: unknown }>(`/tasks/${taskId}`),

  update: (taskId: string, data: unknown) =>
    apiClient.patch<{ task: unknown }>(`/tasks/${taskId}`, data),

  updateStatus: (taskId: string, data: { status: string; position?: number }) =>
    apiClient.patch<{ task: unknown }>(`/tasks/${taskId}/status`, data),

  updateProgress: (taskId: string, data: { completionPercentage: number; reason?: string }) =>
    apiClient.patch<{ task: unknown }>(`/tasks/${taskId}/progress`, data),

  addComment: (taskId: string, content: string) =>
    apiClient.post<{ activity: unknown }>(`/tasks/${taskId}/comment`, { content }),

  getActivity: (taskId: string) =>
    apiClient.get<{ activity: Array<unknown> }>(`/tasks/${taskId}/activity`),

  delete: (taskId: string) => apiClient.delete(`/tasks/${taskId}`),
};

// Email Integration API
export interface EmailAccount {
  id: string;
  teamId: string;
  userId: string;
  provider: 'gmail' | 'zoho';
  emailAddress: string;
  lastSyncAt: string | null;
  syncEnabled: boolean;
  filterLabels: string[];
  filterFrom: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ProcessedEmail {
  id: string;
  emailAccountId: string;
  subject: string | null;
  senderEmail: string | null;
  senderName: string | null;
  receivedAt: string;
  snippet: string | null;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  processingError: string | null;
  extractedTasksCount: number;
}

export const emailApi = {
  // OAuth
  getAuthUrl: (provider: 'gmail' | 'zoho', teamId: string) =>
    apiClient.get<{ authUrl: string }>(`/email/oauth/${provider}/url?teamId=${teamId}`),

  handleOAuthCallback: (
    data: { provider: 'gmail' | 'zoho'; code: string; redirectUri?: string },
    teamId: string
  ) =>
    apiClient.post<{ id: string; emailAddress: string; provider: string; isNew: boolean }>(
      `/email/oauth/callback?teamId=${teamId}`,
      data
    ),

  // Accounts
  listAccounts: (teamId: string) =>
    apiClient.get<EmailAccount[]>(`/email/accounts?teamId=${teamId}`),

  getAccount: (accountId: string) => apiClient.get<EmailAccount>(`/email/accounts/${accountId}`),

  updateAccount: (
    accountId: string,
    data: { syncEnabled?: boolean; filterLabels?: string[]; filterFrom?: string[] }
  ) => apiClient.patch<EmailAccount>(`/email/accounts/${accountId}`, data),

  deleteAccount: (accountId: string) =>
    apiClient.delete<{ deleted: boolean }>(`/email/accounts/${accountId}`),

  // Sync
  triggerSync: (
    accountId: string,
    scope: 'new_only' | 'last_30_days' | 'full_history' = 'new_only'
  ) =>
    apiClient.post<{
      emailsFetched: number;
      emailsProcessed: number;
      tasksCreated: number;
      errors?: string[];
      scope: string;
    }>(`/email/accounts/${accountId}/sync?scope=${scope}`),

  getLabels: (accountId: string) =>
    apiClient.get<Array<{ id: string; name: string }>>(`/email/accounts/${accountId}/labels`),

  // Processed Emails
  listProcessedEmails: (accountId: string, page?: number, limit?: number) =>
    apiClient.get<ProcessedEmail[]>(
      `/email/processed?accountId=${accountId}&page=${page || 1}&limit=${limit || 20}`
    ),

  reprocessEmail: (emailId: string) =>
    apiClient.post<{ status: string }>(`/email/processed/${emailId}/reprocess`),
};

// GitHub Integration API
export interface GitHubInstallation {
  id: string;
  installationId: number;
  accountType: 'user' | 'organization';
  accountLogin: string;
  accountAvatarUrl: string | null;
  suspendedAt: string | null;
  createdAt: string;
}

export interface GitHubRepository {
  id: string;
  installationId: string;
  teamId: string;
  projectId: string | null;
  repoId: number;
  repoFullName: string;
  repoName: string;
  repoOwner: string;
  repoPrivate: boolean;
  defaultBranch: string;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GitHubTaskLink {
  id: string;
  taskId: string;
  repositoryId: string;
  linkType: 'pr' | 'commit' | 'branch';
  prNumber: number | null;
  commitSha: string | null;
  branchName: string | null;
  prTitle: string | null;
  prState: 'open' | 'closed' | 'merged' | null;
  prUrl: string | null;
  detectedFrom: 'branch' | 'pr_title' | 'commit_message' | 'manual';
  createdAt: string;
  updatedAt: string;
}

export interface DiffAnalysisResult {
  id: string;
  taskId: string;
  githubLinkId: string;
  previousCompletion: number;
  newCompletion: number;
  confidenceScore: number;
  aiReasoning: string | null;
  filesAnalyzed: number;
  linesAdded: number;
  linesRemoved: number;
  autoApplied: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export const githubApi = {
  // Installation
  getInstallUrl: (teamId: string) =>
    apiClient.get<{ installUrl: string }>(`/github/install?teamId=${teamId}`),

  listInstallations: (teamId: string) =>
    apiClient.get<GitHubInstallation[]>(`/github/installations?teamId=${teamId}`),

  deleteInstallation: (installationId: string) =>
    apiClient.delete<void>(`/github/installations/${installationId}`),

  // Repositories
  listRepositories: (params: { teamId?: string; installationId?: string; projectId?: string }) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });
    return apiClient.get<GitHubRepository[]>(`/github/repositories?${searchParams.toString()}`);
  },

  getRepository: (repositoryId: string) =>
    apiClient.get<GitHubRepository>(`/github/repositories/${repositoryId}`),

  updateRepository: (
    repositoryId: string,
    data: { syncEnabled?: boolean; projectId?: string | null }
  ) => apiClient.patch<GitHubRepository>(`/github/repositories/${repositoryId}`, data),

  triggerSync: (repositoryId: string) =>
    apiClient.post<{ message: string }>(`/github/repositories/${repositoryId}/sync`),

  // Task Links
  listTaskLinks: (params: { taskId?: string; repositoryId?: string }) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });
    return apiClient.get<GitHubTaskLink[]>(`/github/task-links?${searchParams.toString()}`);
  },

  createTaskLink: (data: {
    taskId: string;
    repositoryId: string;
    linkType: 'pr' | 'commit' | 'branch';
    prNumber?: number;
    commitSha?: string;
    branchName?: string;
  }) => apiClient.post<GitHubTaskLink>('/github/task-links', data),

  deleteTaskLink: (linkId: string) => apiClient.delete<void>(`/github/task-links/${linkId}`),

  triggerDiffAnalysis: (linkId: string, forceReanalyze?: boolean) =>
    apiClient.post<{ message: string; analysisId?: string }>(
      `/github/task-links/${linkId}/analyze`,
      {
        forceReanalyze: forceReanalyze || false,
      }
    ),

  // Diff Analysis
  listDiffAnalysis: (params: { taskId?: string; githubLinkId?: string; autoApplied?: boolean }) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value));
    });
    return apiClient.get<DiffAnalysisResult[]>(`/github/diff-analysis?${searchParams.toString()}`);
  },

  getDiffAnalysis: (analysisId: string) =>
    apiClient.get<DiffAnalysisResult>(`/github/diff-analysis/${analysisId}`),

  applyDiffAnalysis: (analysisId: string, apply: boolean) =>
    apiClient.post<DiffAnalysisResult>(`/github/diff-analysis/${analysisId}/apply`, { apply }),
};

// Reports API
export interface Report {
  id: string;
  projectId: string | null;
  teamId: string;
  type: 'leadership' | 'client';
  title: string;
  content: string;
  format: 'markdown' | 'html' | 'pdf';
  dataSnapshot: ReportDataSnapshot | null;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  aiGenerated: boolean;
  aiConfidence: number | null;
  shareToken: string | null;
  expiresAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface ReportDataSnapshot {
  teamName: string;
  projectName?: string;
  generatedAt: string;
  period: { start: string; end: string };
  totalTasks: number;
  tasksByStatus: Record<string, number>;
  avgCompletionPercentage: number;
  velocityMetrics?: {
    tasksCompletedThisPeriod: number;
    tasksCompletedLastPeriod: number;
    avgDaysToComplete: number;
    projectedCompletion: number;
  };
  engineerStats?: Array<{
    userId: string;
    name: string;
    tasksAssigned: number;
    tasksCompleted: number;
    avgCompletion: number;
  }>;
  blockers?: Array<{
    taskId: string;
    taskTitle: string;
    assigneeName: string | null;
    daysSinceLastUpdate: number;
    status: string;
  }>;
  milestones?: Array<{
    name: string;
    targetDate: string | null;
    status: 'completed' | 'in_progress' | 'upcoming' | 'at_risk';
    progress: number;
    taskCount: number;
  }>;
  deliverables?: Array<{
    name: string;
    description: string;
    status: 'delivered' | 'in_progress' | 'pending';
    deliveredAt: string | null;
  }>;
}

export interface GenerateReportParams {
  teamId: string;
  projectId?: string;
  type: 'leadership' | 'client';
  title: string;
  dateRangeStart: Date | string;
  dateRangeEnd: Date | string;
  includeAiNarrative?: boolean;
  createShareToken?: boolean;
}

export interface ReportListParams {
  teamId: string;
  projectId?: string;
  type?: 'leadership' | 'client';
  limit?: number;
  offset?: number;
}

export const reportsApi = {
  generate: (params: GenerateReportParams) =>
    apiClient.post<{ report: Report }>('/reports/generate', params),

  list: (params: ReportListParams) => {
    const searchParams = new URLSearchParams();
    searchParams.set('teamId', params.teamId);
    if (params.projectId) searchParams.set('projectId', params.projectId);
    if (params.type) searchParams.set('type', params.type);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    return apiClient.get<{ reports: Report[] }>(`/reports?${searchParams.toString()}`);
  },

  get: (reportId: string) => apiClient.get<{ report: Report }>(`/reports/${reportId}`),

  delete: (reportId: string) => apiClient.delete<{ deleted: boolean }>(`/reports/${reportId}`),

  createShareLink: (reportId: string) =>
    apiClient.post<{ shareToken: string; shareUrl: string }>(`/reports/${reportId}/share`),

  revokeShareLink: (reportId: string) =>
    apiClient.delete<{ revoked: boolean }>(`/reports/${reportId}/share`),

  getByShareToken: (shareToken: string) =>
    apiClient.get<{ report: Partial<Report> }>(`/reports/share/${shareToken}`, { skipAuth: true }),
};
